import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, MessageCircleIcon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
import { founder, salon } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { euro, rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { activePromo } from '../../core/rules/promo';
import { canSubscribe } from '../../core/rules/features';
import { useFeatures } from '../../hooks/useFeatures';
import type { Product } from '../../core/types/catalog';
import { btnPrice, btnPriceAmount, btnSecondary } from '../../ui/styles';
// Sterren volgen het echte gemiddelde: een 4,2 toont vier volle sterren, niet vijf.
import { Stars } from '../shared/Stars';
import { oneOffMeta, refillMeta } from './productRules';
type Props = {
  product: Product;
  soldOut: boolean;
  subscribe: boolean;
  onSubscribe: (value: boolean) => void;
  added: boolean;
  onAdd: () => void;
  /** Na toevoegen: de mand openen (de volgende logische stap). */
  onViewCart: () => void;
  giftHint: string | null;
  /** Hieronder: de uitklapblokken met waarom, gebruik en ingrediënten. */
  children: ReactNode;
};

/** Merk, naam, beoordeling, eenmalig of bijvullen, en de knop. */
export function ProductBuyBox({
  product,
  soldOut,
  subscribe,
  onSubscribe,
  added,
  onAdd,
  onViewCart,
  giftHint,
  children
}: Props) {
  const pages = usePages();
  const reduce = useReducedMotion();
  const features = useFeatures();
  const promo = activePromo(product);
  const options = [{
    value: false,
    title: 'Eenmalig',
    meta: oneOffMeta(product)
  }, ...(canSubscribe(features, product) ? [{
    value: true,
    title: `Automatisch bijvullen · ${pages.subscription.name}`,
    meta: refillMeta(product, policyCopy.subscriptionPerks)
  }] : [])];
  return <div className="lg:sticky lg:top-28" data-id="element-8033" data-mp="18w572bzkvoum">
      <p className="text-[14px] text-clay" data-id="element-8034" data-mp="18wx29lp9jkkd">{product.brand}</p>
      <h1 className="mt-2 type-sub text-ink" data-id="element-8035" data-mp="13yc21ma0h0vx">{product.name}</h1>
      {product.reviews > 0 && <a href="#reviews" className="mt-4 inline-flex items-center gap-2 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-8036" data-mp="1hxleqbz0dcv5">
          <Stars value={product.rating} data-id="element-8037" data-mp="1d0la85ht4i65" />
          <span className="sr-only" data-id="element-8038" data-mp="1rupr5k31xtyr">{shopLabels.rating(product)}</span>
          <span aria-hidden="true" data-id="element-8039" data-mp="1mwfvz9hbutd5">
            {rating(product.rating)} · {plural(product.reviews, 'beoordeling', 'beoordelingen')}
          </span>
        </a>}
      {product.note && <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-8040" data-mp="20pnpj6oyxyxh">{product.note}</p>}

      {promo && <p className="mt-5 bg-blush px-4 py-3 text-[14px] leading-relaxed text-ink" data-id="element-8041" data-mp="1wolpinn89iug">
          <span className="font-medium" data-id="element-8042" data-mp="2ajwh9s66dnul">{promo.label}</span>
          {promo.until && <> — {promo.until}</>}
        </p>}

      {/* Alleen een keuze tonen als er iets te kiezen is: zonder bijvullen is "Eenmalig" de enige optie. */}
      {options.length > 1 ? <fieldset className="mt-7 space-y-2" data-id="element-8043" data-mp="2u6hjzr1avofh">
          <legend className="sr-only" data-id="element-8044" data-mp="2fajomaz7ao4b">Aankoopoptie</legend>
          {options.map(o => {
        const active = subscribe === o.value;
        return <label key={o.title} className={`flex cursor-pointer items-start gap-4 border p-4 transition-colors duration-150 ease-soft [&:has(:focus-visible)]:outline [&:has(:focus-visible)]:outline-2 [&:has(:focus-visible)]:outline-offset-2 [&:has(:focus-visible)]:outline-clay ${active ? 'border-ink bg-shell' : 'border-line hover:border-ink/40'}`} data-id="element-8045" data-mp="2k7rxtm2dfhtf">
                <input type="radio" name="purchase" checked={active} onChange={() => onSubscribe(o.value)} className="sr-only" data-id="element-8046" data-mp="1njlyneo7xhmj" />
                <span aria-hidden="true" className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${active ? 'border-ink' : 'border-line'}`} data-id="element-8047" data-mp="1rjw3go09y1zt">
                  {active && <span className="h-2 w-2 rounded-full bg-ink" data-id="element-8048" data-mp="1fi4l80xrz43j" />}
                </span>
                <span className="flex-1" data-id="element-8049" data-mp="1aro86hxv9khp">
                  <span className="flex items-baseline justify-between gap-3" data-id="element-8050" data-mp="1aqmrnxy305sl">
                    <span className="text-[15px] text-ink" data-id="element-8051" data-mp="15rtfc8uj6ku0">{o.title}</span>
                    <span className="type-name text-ink" data-id="element-8052" data-mp="11qztrm4k9i4a">{euro(product.price)}</span>
                  </span>
                  <span className="mt-1 block text-[13px] leading-relaxed text-muted" data-id="element-8053" data-mp="wrn4yvu4q0z2">{o.meta}</span>
                </span>
              </label>;
      })}
        </fieldset> : <p className="mt-7 flex items-baseline justify-between gap-3 border-t border-line pt-4" data-id="element-8054" data-mp="muifon4zlpe1">
          <span className="text-[13px] text-muted" data-id="element-8055" data-mp="2m7mjksgioim3">{options[0]?.meta}</span>
          <span className="type-title text-ink" data-id="element-8056" data-mp="2h808aok91cfx">{euro(product.price)}</span>
        </p>}

      {soldOut ? <div className="mt-5 space-y-2" data-id="element-8057" data-mp="2ppadcsl3tox9">
          <p className="text-center text-[14px] text-muted" data-id="element-8058" data-mp="2poiqc65kozil">{shopLabels.soldOutShort}.</p>
          {/* Voorheen alleen tekst ("app Sarah"); nu een echte knop. */}
          <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${btnSecondary} w-full`} data-id="element-8059" data-mp="2ygu52urbqyiq">
            <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8060" data-mp="33gov6w39zk2q" />
            Vraag {founder.firstName} om een alternatief
            <span className="sr-only" data-id="element-8061" data-mp="26s942vkip2ni">{shopLabels.viaWhatsApp}</span>
          </a>
        </div> : <button type="button" onClick={onAdd} className={`${btnPrice} mt-5`} data-id="element-8062" data-mp="2atkwf8us01ze">
          <AnimatePresence mode="wait" initial={false} data-id="element-8063" data-mp="2fpjekepignrd">
            <motion.span key={added ? 'added' : 'add'} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.15,
          ease
        }} className={`flex w-full items-center gap-4 ${added ? 'justify-center' : 'justify-between'}`} data-id="element-8064" data-mp="2kqgyavq65puz">
              {added ? <span className="flex items-center gap-2" data-id="element-8065" data-mp="31jyj5uongr1s">
                  <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8066" data-mp="3ge3h8m210jhm" />
                  Toegevoegd
                </span> : <>
                  <span data-id="element-8067" data-mp="2npxuxuvbqmfo">In winkelmand</span>
                  <span className={btnPriceAmount} data-id="element-8068" data-mp="2nqqti8k4q5mq">{euro(product.price)}</span>
                </>}
            </motion.span>
          </AnimatePresence>
        </button>}

      {added && !soldOut ? <p className="mt-3 text-center text-[14px]" data-id="element-8069" data-mp="2xi3lr19uaxta">
          <button type="button" onClick={onViewCart} aria-haspopup="dialog" className="min-h-[44px] text-ink underline underline-offset-4 hover:text-clay" data-id="element-8070" data-mp="2soitzqyub02j">
            Bekijk je winkelmand
          </button>
        </p> : giftHint && !soldOut && <p className="mt-3 text-center text-[13px] text-clay" data-id="element-8071" data-mp="fiubxuq364e3">{giftHint}</p>}

      <ul className="mt-6 space-y-2.5 border-t border-line pt-5 text-[14px] text-muted" data-id="element-8072" data-mp="fgrfarufhd0e">
        <li className="flex items-center gap-2.5" data-id="element-8073" data-mp="kavntfjcdt3l">
          <TruckIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8074" data-mp="5lfwnyfwflnd" />
          {policyCopy.delivery}
        </li>
        <li className="flex items-center gap-2.5" data-id="element-8075" data-mp="ak0whwr8o3ba">
          <ShieldCheckIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8076" data-mp="y8tudyrf8l8j" />
          {policyCopy.returns} — ook geopend
        </li>
        {!soldOut && <li className="flex items-center gap-2.5" data-id="element-8077" data-mp="p8xccfzjekmg">
            <MessageCircleIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8078" data-mp="p8dwr5xd08bl" />
            <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-8079" data-mp="tcbwniuidn5p">
              Vraag {founder.firstName} of dit bij je past
              <span className="sr-only" data-id="element-8080" data-mp="9ieywm24mwnh">{shopLabels.viaWhatsApp}</span>
            </a>
          </li>}
      </ul>

      {children}
    </div>;
}